// src/pages/StaffShipments.jsx
import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import Sidebar from '../components/shared/Sidebar';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeShipments, createShipment, updateShipment, deleteShipment,
  subscribeConsignees, createConsignee, updateConsignee,
  logActivity,
} from '../firebase/firestore';

// ── Constants ──────────────────────────────────────────────────
const STATUS_PROGRESS = {
  Pending: 5, 'In Transit': 35, Arrived: 55,
  'Customs Clearance': 70, Cleared: 85, Delivered: 100, 'On Hold': 40,
};
const STATUS_CLASS = {
  Pending: 'pending', 'In Transit': 'transit', Arrived: 'arrived',
  'Customs Clearance': 'customs', Cleared: 'cleared',
  Delivered: 'delivered', 'On Hold': 'held',
};
const STATUS_COLOR = {
  Pending: 'var(--gray-500)', 'In Transit': 'var(--blue)', Arrived: '#639922',
  'Customs Clearance': 'var(--amber)', Cleared: 'var(--teal)',
  Delivered: 'var(--green)', 'On Hold': 'var(--red)',
};
const STAGES = ['Pending', 'In Transit', 'Arrived', 'Customs Clearance', 'Cleared', 'Delivered'];
const STATUSES = ['Pending', 'In Transit', 'Arrived', 'Customs Clearance', 'Cleared', 'Delivered', 'On Hold'];
const DOC_NAMES = [
  'Bill of Lading / AWB', 'Commercial Invoice', 'Packing List',
  'Import Declaration (IDF)', 'Customs Entry (Form C17 / C18)',
  'Certificate of Origin', 'Insurance Certificate',
  'KEBS Pre-Export Verification Certificate', 'Delivery Order', 'Gate Pass',
];

const EMPTY_SHIP = {
  bl: '', consigneeId: '', vessel: '', voyage: '', origin: '', destination: '',
  eta: '', status: 'Pending', packages: '', weight: '', container: '',
  value: '', desc: '', hs: '', remarks: '',
};
const EMPTY_CONS = { name: '', contact: '', phone: '', email: '', address: '', tin: '' };

function isOverdue(s) {
  if (!s.eta || s.status === 'Delivered' || s.status === 'Cleared') return false;
  return new Date(s.eta) < new Date(new Date().toDateString());
}

function ShipBadge({ status }) {
  return <span className={`badge ${STATUS_CLASS[status] || 'pending'}`}>{status}</span>;
}

function ShipProgress({ status }) {
  const pct = STATUS_PROGRESS[status] || 0;
  return (
    <div className="progress-bar" style={{ minWidth: 80 }}>
      <div className="progress-fill" style={{ width: `${pct}%`, background: STATUS_COLOR[status] || 'var(--gray-500)' }} />
    </div>
  );
}

const INPUT_STYLE = {
  fontSize: 13, padding: '8px 11px', borderRadius: 'var(--radius)',
  border: '1px solid var(--gray-300)', background: '#fff',
  color: 'var(--gray-900)', outline: 'none', width: '100%',
};

export default function StaffShipments() {
  const { profile } = useAuth();

  const [shipments, setShipments] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [tab, setTab] = useState('shipments');

  // Shipment table filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterConsignee, setFilterConsignee] = useState('');

  // Shipment modal
  const [showShipModal, setShowShipModal] = useState(false);
  const [shipForm, setShipForm] = useState(EMPTY_SHIP);
  const [editShipId, setEditShipId] = useState(null);
  const [shipSaving, setShipSaving] = useState(false);

  // Detail modal
  const [detailShip, setDetailShip] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [docStatuses, setDocStatuses] = useState({});

  // Consignee modal
  const [showConsModal, setShowConsModal] = useState(false);
  const [consForm, setConsForm] = useState(EMPTY_CONS);
  const [editConsId, setEditConsId] = useState(null);
  const [consSaving, setConsSaving] = useState(false);

  // Reports
  const today = new Date().toISOString().split('T')[0];
  const thirtyAgo = new Date(Date.now() - 30 * 864e5).toISOString().split('T')[0];
  const [repFrom, setRepFrom] = useState(thirtyAgo);
  const [repTo, setRepTo] = useState(today);
  const [repStatus, setRepStatus] = useState('');
  const [repConsignee, setRepConsignee] = useState('');
  const [reportPreview, setReportPreview] = useState(null);

  useEffect(() => {
    const u1 = subscribeShipments(setShipments);
    const u2 = subscribeConsignees(setConsignees);
    return () => { u1(); u2(); };
  }, []);

  const getCons = (id) => consignees.find(c => c.id === id) || { name: '—', contact: '', phone: '', email: '', address: '', tin: '' };
  const isAdmin   = profile?.role === 'admin';
  const canExport = isAdmin || profile?.canExport !== false;


  // Shipment CRUD
  const openAddShip = () => { setEditShipId(null); setShipForm(EMPTY_SHIP); setShowShipModal(true); };
  const openEditShip = (s) => {
    setEditShipId(s.id);
    setShipForm({
      bl: s.bl || '', consigneeId: s.consigneeId || '', vessel: s.vessel || '',
      voyage: s.voyage || '', origin: s.origin || '', destination: s.destination || '',
      eta: s.eta || '', status: s.status || 'Pending', packages: s.packages || '',
      weight: s.weight || '', container: s.container || '', value: s.value || '',
      desc: s.desc || '', hs: s.hs || '', remarks: s.remarks || '',
    });
    setShowShipModal(true);
  };

  const saveShipment = async () => {
    if (!shipForm.bl.trim() || !shipForm.consigneeId || !shipForm.desc.trim()) {
      alert('Please fill in: BL/AWB number, Consignee, and Goods description.');
      return;
    }
    setShipSaving(true);
    const data = {
      ...shipForm,
      packages: Number(shipForm.packages) || 0,
      weight: Number(shipForm.weight) || 0,
      value: Number(shipForm.value) || 0,
      createdBy: profile?.uid,
      createdByName: profile?.name,
    };
    try {
      if (editShipId) {
        await updateShipment(editShipId, data);
        await logActivity({ userId: profile?.uid, userName: profile?.name, action: 'update', entityType: 'shipment', entityId: editShipId, message: `Updated shipment ${shipForm.bl}` });
      } else {
        const ref = await createShipment(data);
        await logActivity({ userId: profile?.uid, userName: profile?.name, action: 'create', entityType: 'shipment', entityId: ref.id, message: `Added shipment ${shipForm.bl}` });
      }
      setShowShipModal(false);
    } finally {
      setShipSaving(false);
    }
  };

  const handleDeleteShip = async (s) => {
    if (!window.confirm(`Delete shipment ${s.bl}? This cannot be undone.`)) return;
    await deleteShipment(s.id);
    await logActivity({ userId: profile?.uid, userName: profile?.name, action: 'delete', entityType: 'shipment', entityId: s.id, message: `Deleted shipment ${s.bl}` });
    if (detailShip?.id === s.id) setDetailShip(null);
  };

  const openDetail = (s) => { setDetailShip(s); setDetailTab('overview'); setDocStatuses(s.documents || {}); };

  const saveDocStatuses = async () => {
    if (!detailShip) return;
    await updateShipment(detailShip.id, { documents: docStatuses });
    setDetailShip(prev => ({ ...prev, documents: docStatuses }));
  };

  // Consignee CRUD
  const openAddCons = () => { setEditConsId(null); setConsForm(EMPTY_CONS); setShowConsModal(true); };
  const openEditCons = (c) => {
    setEditConsId(c.id);
    setConsForm({ name: c.name || '', contact: c.contact || '', phone: c.phone || '', email: c.email || '', address: c.address || '', tin: c.tin || '' });
    setShowConsModal(true);
  };
  const saveCons = async () => {
    if (!consForm.name.trim()) { alert('Company name is required.'); return; }
    setConsSaving(true);
    try {
      if (editConsId) {
        await updateConsignee(editConsId, { ...consForm, updatedBy: profile?.uid });
      } else {
        await createConsignee({ ...consForm, createdBy: profile?.uid, createdByName: profile?.name });
      }
      setShowConsModal(false);
    } finally {
      setConsSaving(false);
    }
  };

  // Filtered shipments
  const filtered = shipments.filter(s => {
    const c = getCons(s.consigneeId);
    const q = search.toLowerCase();
    return (
      (!q || (s.bl + (s.vessel || '') + (s.desc || '') + c.name + (s.origin || '')).toLowerCase().includes(q)) &&
      (!filterStatus || s.status === filterStatus) &&
      (!filterConsignee || s.consigneeId === filterConsignee)
    );
  });

  // Excel
  const buildRows = (data) => data.map(s => {
    const c = getCons(s.consigneeId);
    return {
      'BL / AWB Number': s.bl, 'Consignee Company': c.name, 'Contact Person': c.contact,
      'Phone': c.phone, 'Email': c.email, 'TIN / VAT': c.tin,
      'Vessel / Flight': s.vessel || '', 'Voyage No': s.voyage || '',
      'Origin': s.origin || '', 'Destination': s.destination || '',
      'ETA': s.eta || '', 'Status': s.status, 'Goods Description': s.desc,
      'HS Code(s)': s.hs || '', 'No. of Packages': s.packages || 0,
      'Gross Weight (kg)': s.weight || 0, 'Container / Marks': s.container || '',
      'Freight Value (USD)': s.value || 0, 'Remarks': s.remarks || '',
    };
  });
  const exportXlsx = (data, filename) => {
    if (!data.length) { alert('No records to export.'); return; }
    const rows = buildRows(data);
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Shipments');
    XLSX.writeFile(wb, filename);
  };

  const getReportData = () => shipments.filter(s => {
    const d = s.eta || '';
    return (!repFrom || d >= repFrom) && (!repTo || d <= repTo) &&
      (!repStatus || s.status === repStatus) && (!repConsignee || s.consigneeId === repConsignee);
  });

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">

        {/* Topbar */}
        <div className="topbar">
          <div>
            <div className="page-title">Shipments</div>
            <div className="page-sub">Consignee goods monitoring</div>
          </div>
          <div className="btn-group">
            {tab === 'shipments'  && <button className="btn primary" onClick={openAddShip}><i className="ti ti-plus" /> Add shipment</button>}
            {tab === 'consignees' && <button className="btn primary" onClick={openAddCons}><i className="ti ti-plus" /> Add consignee</button>}
          </div>
        </div>

        {/* Nav tabs */}
        <div style={{ background: '#fff', borderBottom: '1px solid var(--gray-200)', padding: '0 20px', display: 'flex', overflowX: 'auto', flexShrink: 0 }}>
          {[
            { key: 'shipments',  icon: 'ti-packages',       label: 'Shipments' },
            { key: 'consignees', icon: 'ti-building-store', label: 'Consignees' },
            { key: 'reports',    icon: 'ti-chart-bar',      label: 'Reports & Export' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '11px 18px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: tab === t.key ? '2.5px solid var(--blue)' : '2.5px solid transparent',
              color: tab === t.key ? 'var(--blue)' : 'var(--gray-600)',
              fontWeight: tab === t.key ? 600 : 400,
              display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}>
              <i className={`ti ${t.icon}`} /> {t.label}
            </button>
          ))}
        </div>

        <div className="scroll">

          {/* ════ SHIPMENTS ════ */}
          {tab === 'shipments' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title"><i className="ti ti-packages" style={{ color: 'var(--blue)' }} /> All shipments</span>
                <button className="btn primary" onClick={openAddShip}><i className="ti ti-plus" /> Add shipment</button>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                <input type="text" placeholder="Search BL, consignee, vessel, goods…" value={search} onChange={e => setSearch(e.target.value)}
                  style={{ ...INPUT_STYLE, width: 240, height: 36 }} />
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...INPUT_STYLE, width: 'auto', height: 36 }}>
                  <option value="">All statuses</option>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
                <select value={filterConsignee} onChange={e => setFilterConsignee(e.target.value)} style={{ ...INPUT_STYLE, width: 'auto', height: 36 }}>
                  <option value="">All consignees</option>
                  {consignees.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {canExport && (
                  <button className="btn success" onClick={() => exportXlsx(filtered, `GACH_Shipments_${today}.xlsx`)}>
                    <i className="ti ti-file-spreadsheet" /> Export to Excel
                  </button>
                )}
              </div>
              {filtered.length === 0
                ? <div className="empty-state"><i className="ti ti-search" /><p>No shipments match your search</p></div>
                : <>
                  <div className="table-wrap">
                    <table style={{ minWidth: 900 }}>
                      <thead><tr>
                        <th>#</th><th>BL / AWB No.</th><th>Consignee</th><th>Vessel / Flight</th>
                        <th>Origin</th><th>Goods description</th><th>ETA</th>
                        <th>Status</th><th>Progress</th><th>Actions</th>
                      </tr></thead>
                      <tbody>{filtered.map((s, i) => {
                        const c = getCons(s.consigneeId);
                        const over = isOverdue(s);
                        return (
                          <tr key={s.id} style={over ? { background: '#fffbf0' } : {}}>
                            <td style={{ color: 'var(--gray-500)', fontSize: 11 }}>{i + 1}</td>
                            <td>
                              <strong style={{ cursor: 'pointer', color: 'var(--blue)' }} onClick={() => openDetail(s)}>{s.bl}</strong>
                              {over && <span title="Past ETA" style={{ marginLeft: 4 }}>⚠️</span>}
                            </td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{c.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.contact}</div>
                            </td>
                            <td style={{ fontSize: 12 }}>{s.vessel || '—'}<br /><span style={{ color: 'var(--gray-500)' }}>{s.voyage || ''}</span></td>
                            <td style={{ fontSize: 12 }}>{s.origin || '—'}</td>
                            <td style={{ fontSize: 12, maxWidth: 180, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={s.desc}>{s.desc}</td>
                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{s.eta || '—'}</td>
                            <td><ShipBadge status={s.status} /></td>
                            <td style={{ minWidth: 90 }}>
                              <ShipProgress status={s.status} />
                              <div style={{ fontSize: 10, color: 'var(--gray-500)', textAlign: 'right', marginTop: 2 }}>{STATUS_PROGRESS[s.status] || 0}%</div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button className="btn sm" title="View" onClick={() => openDetail(s)}><i className="ti ti-eye" /></button>
                                <button className="btn sm" title="Edit" onClick={() => openEditShip(s)}><i className="ti ti-edit" /></button>
                                <button className="btn sm danger" title="Delete" onClick={() => handleDeleteShip(s)}><i className="ti ti-trash" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                  <div style={{ paddingTop: 10, fontSize: 12, color: 'var(--gray-500)' }}>
                    Showing {filtered.length} of {shipments.length} shipment{shipments.length !== 1 ? 's' : ''}
                  </div>
                </>}
            </div>
          )}

          {/* ════ CONSIGNEES ════ */}
          {tab === 'consignees' && <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}><i className="ti ti-building-store" style={{ color: 'var(--blue)' }} /> Consignee directory</div>
              <button className="btn primary" onClick={openAddCons}><i className="ti ti-plus" /> Add consignee</button>
            </div>
            {consignees.length === 0
              ? <div className="empty-state"><i className="ti ti-building-store" /><p>No consignees yet. Add the first one.</p></div>
              : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {consignees.map(c => {
                  const total = shipments.filter(s => s.consigneeId === c.id).length;
                  const active = shipments.filter(s => s.consigneeId === c.id && !['Delivered', 'Cleared'].includes(s.status)).length;
                  const initials = c.name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
                  const canEdit = c.createdBy === profile?.uid;
                  return (
                    <div key={c.id} className="card" style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <div className="avatar" style={{ background: 'var(--blue-light)', color: 'var(--blue)', fontSize: 14, fontWeight: 700, width: 44, height: 44 }}>{initials}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>TIN: {c.tin || 'Not set'}</div>
                        </div>
                        {(isAdmin || canEdit) && <button className="btn sm" onClick={() => openEditCons(c)}><i className="ti ti-edit" /></button>}
                      </div>
                      <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 10 }}>
                        {[
                          ['ti-user',    c.contact || '—', null],
                          ['ti-phone',   c.phone   || '—', null],
                          ['ti-mail',    c.email   || '—', 'var(--blue)'],
                          ['ti-map-pin', c.address || '—', null],
                        ].map(([icon, val, color]) => (
                          <div key={icon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--gray-200)', fontSize: 13, gap: 8 }}>
                            <i className={`ti ${icon}`} style={{ color: 'var(--gray-500)', flexShrink: 0 }} />
                            <span style={{ color: color || 'inherit', textAlign: 'right', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
                        <span className="badge progress">{total} shipment{total !== 1 ? 's' : ''}</span>
                        <span className="badge pending">{active} active</span>
                      </div>
                    </div>
                  );
                })}
              </div>}
          </>}

          {/* ════ REPORTS ════ */}
          {tab === 'reports' && (
            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-filter" style={{ color: 'var(--blue)' }} /> Report filters</span></div>
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group"><label>From date</label><input type="date" value={repFrom} onChange={e => setRepFrom(e.target.value)} /></div>
                <div className="form-group"><label>To date</label><input type="date" value={repTo} onChange={e => setRepTo(e.target.value)} /></div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={repStatus} onChange={e => setRepStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Consignee</label>
                  <select value={repConsignee} onChange={e => setRepConsignee(e.target.value)}>
                    <option value="">All consignees</option>
                    {consignees.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="btn-group" style={{ marginBottom: 20 }}>
                <button className="btn primary" onClick={() => setReportPreview(getReportData())}><i className="ti ti-eye" /> Preview report</button>
                {canExport && (
                  <button className="btn success" onClick={() => exportXlsx(getReportData(), `GACH_Report_${today}.xlsx`)}>
                    <i className="ti ti-file-spreadsheet" /> Download Excel
                  </button>
                )}
                <button className="btn" onClick={() => window.print()}><i className="ti ti-printer" /> Print</button>
              </div>
              {reportPreview !== null && (
                reportPreview.length === 0
                  ? <div className="empty-state"><i className="ti ti-mood-empty" /><p>No shipments match the selected filters.</p></div>
                  : <>
                    <div className="metrics-grid" style={{ marginBottom: 16 }}>
                      <div className="metric blue"><div className="metric-num">{reportPreview.length}</div><div className="metric-label">Records</div></div>
                      <div className="metric green"><div className="metric-num">${reportPreview.reduce((a, s) => a + (s.value || 0), 0).toLocaleString()}</div><div className="metric-label">Total value</div></div>
                      <div className="metric blue"><div className="metric-num">{reportPreview.reduce((a, s) => a + (s.packages || 0), 0).toLocaleString()}</div><div className="metric-label">Packages</div></div>
                      <div className="metric purple"><div className="metric-num">{reportPreview.reduce((a, s) => a + (s.weight || 0), 0).toLocaleString()} kg</div><div className="metric-label">Gross weight</div></div>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>#</th><th>BL/AWB</th><th>Consignee</th><th>Vessel</th><th>Origin</th><th>Goods</th><th>ETA</th><th>Status</th><th>Pkgs</th><th>Weight</th><th>Value (USD)</th></tr></thead>
                        <tbody>{reportPreview.map((s, i) => (
                          <tr key={s.id}>
                            <td>{i + 1}</td><td><strong>{s.bl}</strong></td>
                            <td>{getCons(s.consigneeId).name}</td>
                            <td style={{ fontSize: 12 }}>{s.vessel || '—'}</td>
                            <td style={{ fontSize: 12 }}>{s.origin || '—'}</td>
                            <td style={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{s.desc}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>{s.eta || '—'}</td>
                            <td><ShipBadge status={s.status} /></td>
                            <td>{s.packages || '—'}</td>
                            <td>{s.weight ? `${Number(s.weight).toLocaleString()} kg` : '—'}</td>
                            <td>{s.value ? `$${Number(s.value).toLocaleString()}` : '—'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ════ SHIPMENT MODAL ════ */}
      {showShipModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowShipModal(false)}>
          <div className="modal" style={{ width: 620 }}>
            <div className="modal-header">
              <span className="modal-title">
                <i className={`ti ${editShipId ? 'ti-edit' : 'ti-packages'}`} style={{ color: 'var(--blue)', marginRight: 8 }} />
                {editShipId ? 'Edit shipment' : 'Add new shipment'}
              </span>
              <button className="btn sm" onClick={() => setShowShipModal(false)}><i className="ti ti-x" /></button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>BL / AWB number *</label>
                <input type="text" value={shipForm.bl} onChange={e => setShipForm(p => ({ ...p, bl: e.target.value }))} placeholder="e.g. MSCU1234567" />
              </div>
              <div className="form-group">
                <label>Consignee *</label>
                <select value={shipForm.consigneeId} onChange={e => setShipForm(p => ({ ...p, consigneeId: e.target.value }))}>
                  <option value="">Select consignee…</option>
                  {consignees.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Vessel / flight name</label>
                <input type="text" value={shipForm.vessel} onChange={e => setShipForm(p => ({ ...p, vessel: e.target.value }))} placeholder="e.g. MSC ANNA" />
              </div>
              <div className="form-group">
                <label>Voyage / flight number</label>
                <input type="text" value={shipForm.voyage} onChange={e => setShipForm(p => ({ ...p, voyage: e.target.value }))} placeholder="e.g. 240W" />
              </div>
              <div className="form-group">
                <label>Origin port / airport</label>
                <input type="text" value={shipForm.origin} onChange={e => setShipForm(p => ({ ...p, origin: e.target.value }))} placeholder="e.g. Shanghai (CNSHA)" />
              </div>
              <div className="form-group">
                <label>Destination</label>
                <input type="text" value={shipForm.destination} onChange={e => setShipForm(p => ({ ...p, destination: e.target.value }))} placeholder="e.g. Mombasa (KEMBA)" />
              </div>
              <div className="form-group">
                <label>ETA</label>
                <input type="date" value={shipForm.eta} onChange={e => setShipForm(p => ({ ...p, eta: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select value={shipForm.status} onChange={e => setShipForm(p => ({ ...p, status: e.target.value }))}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>No. of packages</label>
                <input type="number" min="0" value={shipForm.packages} onChange={e => setShipForm(p => ({ ...p, packages: e.target.value }))} placeholder="e.g. 24" />
              </div>
              <div className="form-group">
                <label>Gross weight (kg)</label>
                <input type="number" min="0" value={shipForm.weight} onChange={e => setShipForm(p => ({ ...p, weight: e.target.value }))} placeholder="e.g. 1200" />
              </div>
              <div className="form-group">
                <label>Container / marks</label>
                <input type="text" value={shipForm.container} onChange={e => setShipForm(p => ({ ...p, container: e.target.value }))} placeholder="e.g. MSCU1234567/1" />
              </div>
              <div className="form-group">
                <label>Freight value (USD)</label>
                <input type="number" min="0" value={shipForm.value} onChange={e => setShipForm(p => ({ ...p, value: e.target.value }))} placeholder="e.g. 5000" />
              </div>
              <div className="form-group full">
                <label>Goods description *</label>
                <input type="text" value={shipForm.desc} onChange={e => setShipForm(p => ({ ...p, desc: e.target.value }))} placeholder="e.g. Industrial machinery parts" />
              </div>
              <div className="form-group full">
                <label>HS code(s)</label>
                <input type="text" value={shipForm.hs} onChange={e => setShipForm(p => ({ ...p, hs: e.target.value }))} placeholder="e.g. 8466.10.00, 8466.20.00" />
              </div>
              <div className="form-group full">
                <label>Remarks / notes</label>
                <textarea value={shipForm.remarks} onChange={e => setShipForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Special handling, customs notes, alerts…" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowShipModal(false)}>Cancel</button>
              <button className="btn primary" onClick={saveShipment} disabled={shipSaving}>
                <i className="ti ti-device-floppy" /> {shipSaving ? 'Saving…' : 'Save shipment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ DETAIL MODAL ════ */}
      {detailShip && (() => {
        const s = shipments.find(x => x.id === detailShip.id) || detailShip;
        const c = getCons(s.consigneeId);
        const currIdx = STAGES.indexOf(s.status);
        return (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetailShip(null)}>
            <div className="modal" style={{ width: 640 }}>
              <div className="modal-header">
                <span className="modal-title"><i className="ti ti-file-text" style={{ color: 'var(--blue)', marginRight: 8 }} />{s.bl}</span>
                <button className="btn sm" onClick={() => setDetailShip(null)}><i className="ti ti-x" /></button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <ShipBadge status={s.status} />
                <div style={{ flex: 1 }}><ShipProgress status={s.status} /></div>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{STATUS_PROGRESS[s.status] || 0}% complete</span>
              </div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {[['overview', 'ti-info-circle', 'Overview'], ['timeline', 'ti-timeline', 'Timeline'], ['docs', 'ti-files', 'Documents']].map(([key, icon, label]) => (
                  <button key={key} onClick={() => setDetailTab(key)} style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 'var(--radius)',
                    border: detailTab === key ? '1px solid var(--blue-light)' : '1px solid transparent',
                    background: detailTab === key ? 'var(--blue-light)' : 'none',
                    color: detailTab === key ? 'var(--blue)' : 'var(--gray-600)',
                  }}>
                    <i className={`ti ${icon}`} /> {label}
                  </button>
                ))}
              </div>

              {detailTab === 'overview' && (
                <div>
                  {[
                    ['Consignee', c.name], ['Contact person', c.contact || '—'],
                    ['Phone', c.phone || '—'], ['Email', c.email || '—'],
                    ['Vessel / flight', `${s.vessel || '—'} ${s.voyage || ''}`],
                    ['Origin → Destination', `${s.origin || '—'} → ${s.destination || '—'}`],
                    ['ETA', s.eta || '—'], ['Goods description', s.desc],
                    ['HS code(s)', s.hs || '—'], ['No. of packages', s.packages || '—'],
                    ['Gross weight', s.weight ? `${Number(s.weight).toLocaleString()} kg` : '—'],
                    ['Container / marks', s.container || '—'],
                    ['Freight value', s.value ? `USD ${Number(s.value).toLocaleString()}` : '—'],
                    ['Remarks', s.remarks || '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: 13, gap: 12 }}>
                      <span style={{ color: 'var(--gray-500)', flexShrink: 0, minWidth: 140 }}>{k}</span>
                      <span style={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {detailTab === 'timeline' && (
                <div>
                  {STAGES.map((st, i) => {
                    const done = currIdx > -1 && i < currIdx;
                    const active = i === currIdx;
                    const last = i === STAGES.length - 1;
                    return (
                      <div key={st} style={{ display: 'flex', gap: 14, minHeight: 48 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
                          <div style={{
                            width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                            background: done ? 'var(--green)' : active ? 'var(--blue)' : '#fff',
                            border: `2px solid ${done ? 'var(--green)' : active ? 'var(--blue)' : 'var(--gray-300)'}`,
                            boxShadow: active ? '0 0 0 3px rgba(24,95,165,.2)' : 'none',
                          }} />
                          {!last && <div style={{ flex: 1, width: 2, background: 'var(--gray-200)', margin: '2px auto' }} />}
                        </div>
                        <div style={{ paddingBottom: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: active ? 'var(--blue)' : done ? 'var(--green)' : 'var(--gray-500)' }}>{st}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{active ? 'Current status' : done ? 'Completed' : 'Upcoming'}</div>
                        </div>
                      </div>
                    );
                  })}
                  {s.status === 'On Hold' && (
                    <div className="alert warning" style={{ marginTop: 12 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>This shipment is on hold and is not on the standard progression timeline.</span>
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'docs' && (
                <div>
                  <div className="alert info" style={{ marginBottom: 12 }}>
                    <i className="ti ti-info-circle" />
                    <span>Track document status below. Actual files should be stored in your document management system.</span>
                  </div>
                  {DOC_NAMES.map(d => (
                    <div key={d} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', gap: 12 }}>
                      <span style={{ fontSize: 13 }}>{d}</span>
                      <select value={docStatuses[d] || 'Pending'} onChange={e => setDocStatuses(p => ({ ...p, [d]: e.target.value }))}
                        style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--gray-300)', background: '#fff' }}>
                        {['Pending', 'Received', 'Submitted', 'Approved', 'N/A'].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn primary sm" onClick={saveDocStatuses}><i className="ti ti-device-floppy" /> Save document statuses</button>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button className="btn danger" onClick={() => handleDeleteShip(s)}><i className="ti ti-trash" /> Delete</button>
                <button className="btn" onClick={() => { setDetailShip(null); openEditShip(s); }}><i className="ti ti-edit" /> Edit</button>
                {canExport && (
                  <button className="btn success" onClick={() => exportXlsx([s], `GACH_${s.bl}_${today}.xlsx`)}>
                    <i className="ti ti-file-spreadsheet" /> Export record
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ════ CONSIGNEE MODAL ════ */}
      {showConsModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowConsModal(false)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">
                <i className="ti ti-building-store" style={{ color: 'var(--blue)', marginRight: 8 }} />
                {editConsId ? 'Edit consignee' : 'Add consignee'}
              </span>
              <button className="btn sm" onClick={() => setShowConsModal(false)}><i className="ti ti-x" /></button>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Company name *</label>
                <input type="text" value={consForm.name} onChange={e => setConsForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ABC Traders Ltd" />
              </div>
              <div className="form-group full">
                <label>Contact person</label>
                <input type="text" value={consForm.contact} onChange={e => setConsForm(p => ({ ...p, contact: e.target.value }))} placeholder="Full name" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" value={consForm.phone} onChange={e => setConsForm(p => ({ ...p, phone: e.target.value }))} placeholder="+256…" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={consForm.email} onChange={e => setConsForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" />
              </div>
              <div className="form-group full">
                <label>Address</label>
                <textarea value={consForm.address} onChange={e => setConsForm(p => ({ ...p, address: e.target.value }))} placeholder="Physical / postal address" />
              </div>
              <div className="form-group full">
                <label>TIN / VAT number</label>
                <input type="text" value={consForm.tin} onChange={e => setConsForm(p => ({ ...p, tin: e.target.value }))} placeholder="Tax identification number" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowConsModal(false)}>Cancel</button>
              <button className="btn primary" onClick={saveCons} disabled={consSaving}>
                <i className="ti ti-device-floppy" /> {consSaving ? 'Saving…' : 'Save consignee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
